import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState, ErrorState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Search, Send, Loader2, ExternalLink, CheckCircle2, AlertTriangle, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Flooring() {
  const qc = useQueryClient();
  const [ref, setRef] = useState("");
  const [looking, setLooking] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null);

  const history = useQuery({ queryKey: ["flooring-jobs"], queryFn: async () => (await client.get("/flooring/jobs")).data });

  const lookup = async () => {
    const q = ref.trim();
    if (!q) return;
    setLooking(true); setForm(null); setMeta(null);
    try {
      const { data } = await client.get("/flooring/lookup", { params: { ref: q } });
      setForm({
        ...data.prefill,
        items: [],
        flooring_colour: data.prefill.flooring_colour || "",
        trims_colour: "Match Door Handles",
        quads_required: !!data.prefill.scotia,
        quantity_sqft: "",
        levelling_allowance: "", access_code: "", assigned_team: "",
        job_date: "", job_end_date: "", levelling_date: "", levelling_end_date: "",
        custom_components: [], extra_notes: "",
      });
      setMeta({ enabled: data.enabled, already_sent: data.already_sent, source: data.prefill.source, is_flooring: data.prefill.is_flooring, teams: data.teams || [], colours: data.flooring_colours || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally { setLooking(false); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setComp = (i, k, v) => setForm((f) => ({ ...f, custom_components: f.custom_components.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const addComp = () => setForm((f) => ({ ...f, custom_components: [...(f.custom_components || []), { label: "", value: "" }] }));
  const delComp = (i) => setForm((f) => ({ ...f, custom_components: f.custom_components.filter((_, idx) => idx !== i) }));

  const send = async () => {
    setSending(true);
    try {
      const { data } = await client.post("/flooring/send", form);
      toast.success(data.status === "already_sent" ? "Already created in Job Management" : "Flooring job created");
      qc.invalidateQueries({ queryKey: ["flooring-jobs"] });
      setMeta((m) => ({ ...m, already_sent: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create job");
    } finally { setSending(false); }
  };

  const list = history.data || [];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Flooring" subtitle="Create a job in TilesMate Job Management from a quote" testid="flooring-header" />

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <Label className="mb-1.5 block">Quote number</Label>
        <div className="flex gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={ref} onChange={(e) => setRef(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") lookup(); }} placeholder="e.g. INV-06082026-D2A" className="pl-9 uppercase" data-testid="flooring-ref-input" />
          </div>
          <Button onClick={lookup} disabled={looking} data-testid="flooring-lookup-btn" className="gap-1.5">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Look up</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Enter the quote number issued when a quote was pushed to inFlow. We'll pull the address and selections so you can review before creating the job.</p>
      </div>

      {form && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="flooring-form">
          {meta?.source && <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5" />{meta.source}</div>}
          {meta && !meta.is_flooring && <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#D97706]/10 px-3 py-2 text-xs font-medium text-warning"><AlertTriangle className="h-4 w-4" />This quote isn't from a Flooring stage — double-check the selections before sending.</div>}
          {meta && meta.enabled === false && <div className="mb-3 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground" data-testid="flooring-not-configured"><AlertTriangle className="h-4 w-4" />The Job Management connection isn't configured yet — add the secret to enable sending.</div>}
          {meta?.already_sent?.job_id && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-[#D1FAE5] px-3 py-2 text-sm text-[#065F46]" data-testid="flooring-already-sent">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />Job already created ({meta.already_sent.external_ref})</span>
              {meta.already_sent.url && <a href={meta.already_sent.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold hover:underline">Open <ExternalLink className="h-3.5 w-3.5" /></a>}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5"><Label>Site address</Label><Input value={form.site_address} onChange={(e) => setField("site_address", e.target.value)} data-testid="flooring-address" /></div>
            <div className="grid gap-1.5"><Label>Customer / builder</Label><Input value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} data-testid="flooring-name" /></div>
            <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.customer_phone} onChange={(e) => setField("customer_phone", e.target.value)} data-testid="flooring-phone" /></div>
            <div className="grid gap-1.5"><Label>Email</Label><Input value={form.customer_email} onChange={(e) => setField("customer_email", e.target.value)} data-testid="flooring-email" /></div>
          </div>
          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="flooring-job-details">
            <Label className="mb-3 block font-semibold">Job details for Job Management</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Flooring colour</Label>
                <Select value={form.flooring_colour || "none"} onValueChange={(v) => setField("flooring_colour", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="flooring-colour"><SelectValue placeholder="Select a colour" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a colour</SelectItem>
                    {(meta?.colours || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Trims colour</Label><Input value={form.trims_colour} onChange={(e) => setField("trims_colour", e.target.value)} data-testid="trims-colour" /></div>
              <div className="grid gap-1.5"><Label>Quantity (sqm)</Label><Input value={form.quantity_sqft} onChange={(e) => setField("quantity_sqft", e.target.value)} placeholder="e.g. 86" data-testid="quantity-sqft" /></div>
              <div className="grid gap-1.5"><Label>Levelling allowance</Label><Input value={form.levelling_allowance} onChange={(e) => setField("levelling_allowance", e.target.value)} placeholder="e.g. 2 bags" data-testid="levelling-allowance" /></div>
              <div className="grid gap-1.5"><Label>Site access code</Label><Input value={form.access_code} onChange={(e) => setField("access_code", e.target.value)} data-testid="access-code" /></div>
              <div className="grid gap-1.5">
                <Label>Assigned team</Label>
                <Select value={form.assigned_team || "none"} onValueChange={(v) => setField("assigned_team", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="flooring-team"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {(meta?.teams || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(meta?.teams || []).length === 0 && <span className="text-[11px] text-muted-foreground">Add portal team names in Settings → Flooring Job Sync.</span>}
              </div>
              <label className="flex items-center gap-2 self-end pb-2 text-sm"><input type="checkbox" checked={!!form.quads_required} onChange={(e) => setField("quads_required", e.target.checked)} data-testid="quads-required" className="h-4 w-4" />Quads / Scotia required</label>
              <div className="grid gap-1.5"><Label>Install date</Label><Input type="date" value={form.job_date} onChange={(e) => setField("job_date", e.target.value)} data-testid="job-date" /></div>
              <div className="grid gap-1.5"><Label>Install end date</Label><Input type="date" value={form.job_end_date} onChange={(e) => setField("job_end_date", e.target.value)} data-testid="job-end-date" /></div>
              <div className="grid gap-1.5"><Label>Levelling date</Label><Input type="date" value={form.levelling_date} onChange={(e) => setField("levelling_date", e.target.value)} data-testid="levelling-date" /></div>
              <div className="grid gap-1.5"><Label>Levelling end date</Label><Input type="date" value={form.levelling_end_date} onChange={(e) => setField("levelling_end_date", e.target.value)} data-testid="levelling-end-date" /></div>
            </div>
            <div className="mt-4">
              <Label className="mb-2 block">Custom components</Label>
              <div className="space-y-2">
                {(form.custom_components || []).map((c, i) => (
                  <div key={i} className="flex gap-2" data-testid={`custom-comp-${i}`}>
                    <Input value={c.label} onChange={(e) => setComp(i, "label", e.target.value)} placeholder="Label e.g. Stair nosing" />
                    <Input value={c.value} onChange={(e) => setComp(i, "value", e.target.value)} placeholder="Value e.g. 3 x black" />
                    <button onClick={() => delComp(i)} data-testid={`del-comp-${i}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 text-muted-foreground hover:border-error/40 hover:text-error"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addComp} data-testid="add-comp-btn" className="gap-1.5"><Plus className="h-4 w-4" />Add component</Button>
              </div>
            </div>
            <div className="mt-4 grid gap-1.5"><Label>Extra notes</Label><Textarea value={form.extra_notes} onChange={(e) => setField("extra_notes", e.target.value)} placeholder="Free-text notes appended in the portal" data-testid="extra-notes" /></div>
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={send} disabled={sending || meta?.enabled === false} data-testid="flooring-send-btn" className="gap-1.5">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Create job in Job Management</Button>
          </div>
        </div>
      )}

      <h2 className="mb-3 font-display text-base font-bold text-ink">Recent flooring jobs</h2>
      {history.isError ? (
        <ErrorState onRetry={() => history.refetch()} message="Couldn't load history." />
      ) : list.length === 0 ? (
        <EmptyState icon={Layers} title="No flooring jobs yet" hint="Look up a quote number above to create your first flooring job." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="flooring-history">
          {list.map((j) => (
            <div key={j.id} data-testid={`flooring-history-${j.external_ref}`} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0">
              <Layers className="h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-ink">{j.customer_name || j.external_ref}</div>
                <div className="truncate text-xs text-muted-foreground">{j.external_ref} · {j.site_address || "—"} · {j.item_count} item(s) · {j.sent_by_name}</div>
              </div>
              {j.url && <a href={j.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Open <ExternalLink className="h-3.5 w-3.5" /></a>}
              <span className="shrink-0 text-xs text-muted-foreground">{fmtDate(j.created_at, "d MMM")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
